import { TopoSorter } from '@attain-apps/data-utils'
import type { TypeChecker } from '@attain-apps/type-guards'

import { Expression } from '../../../../expression'
import * as expressionNode from '../../../../node'
import type { FnHelper } from '../../../helper'
import { Key } from '../../../key'
import type { CreateObject } from '../../create-object'
import type { CreateTuple } from '../../create-tuple'
import { GetProperty } from '../../get-property'

export function withProperties<
  TFn extends ApplicableFn,
  TNodeClass extends ApplicableNodeClass,
>({
  fnKey,
  isFn,
  nodeClass,
}: withProperties.Params<TFn, TNodeClass>): FnHelper<TFn>['createNode']
export function withProperties({
  fnKey,
  isFn,
  nodeClass,
}: withProperties.Params<ApplicableFn, ApplicableNodeClass>): NodeFactory {
  return (fn, context): ReturnNode => {
    const expressions = fn[fnKey]
    const isList = fnKey === Key.CreateTuple
    const expectedPropertyType = isList ? 'number' : 'string'
    const arg = (isList ? [] : {}) as Record<
      string | number,
      expressionNode.Node
    >
    const result = (isList ? [] : {}) as Record<string | number, unknown>

    /**
     * Topographically sorts the fields of {@link expressionRecord} to ensure
     * we create fields that reference other fields after those other fields
     *
     * @example
     * const expression = {
     *   '{}': {
     *     third: { '@>': 'second' },
     *     first: { '+': [ 1, 2 ] },
     *     second: { '@>': 'first' },
     *   }
     * }
     *
     * const fields = fieldSorter.sort() // [ 'first', 'second', 'third' ]
     */
    const fieldSorter = new TopoSorter<string>()
    for (const k of Object.keys(expressions)) {
      const expression = expressions[k]
      if (expression === undefined) {
        continue
      }
      const dependencies: string[] = []
      for (const {
        expression: nestedExpression,
        prune,
      } of Expression.allWithin(expression)) {
        if (GetProperty.isGetProperty(nestedExpression)) {
          const property = nestedExpression[Key.GetProperty]
          // GetProperty: string/number => references another field on this object/list
          if (typeof property === expectedPropertyType) {
            // We can convert numbers to strings because JS treats them the same
            // when indexing
            dependencies.push(property.toString())
          }
        } else if (isFn(nestedExpression)) {
          // If we encounter a nested expression of this type (CreateObject or
          // CreateTuple) then that will have its own properties for
          // GetProperty, so we don't want to go into that expression
          prune()
        }
      }
      fieldSorter.add(k, { dependencies, label: k })
    }

    // We have new sibling expressions, so we start with empty and populate as
    // we go
    const currentSiblingExpressionNodes: Mutable<
      typeof context.siblingExpressionNodes
    > = {}
    const childContext: typeof context = {
      ...context,
      siblingExpressionNodes: currentSiblingExpressionNodes,
    }
    /**
     * We cannot return a value if at least one of the fields is not a value
     */
    let atLeastOneNonValueNode = false
    for (const k of fieldSorter.sort().items) {
      const expression = expressions[k]
      const node =
        expression === undefined
          ? new expressionNode.ForValue(undefined)
          : expressionNode.from(expression, childContext)
      arg[k] = node
      // Makes this field accessible to future sibling GetProperty expressions
      currentSiblingExpressionNodes[k] = node

      if (!atLeastOneNonValueNode) {
        if (expressionNode.ForValue.isForValue(node)) {
          // If we have yet another value, add it to our result

          // NOTE: It is important that the property value is not undefined, or
          // else the entire property won't show up in JSON
          result[k] = node.value ?? null
        } else {
          // If this is our first non-value, we will no longer be able to
          // yield a value result
          atLeastOneNonValueNode = true
        }
      }
    }

    // NOTE: getting TypeScript to understand that we can pass `arg` as the
    // constructor param is not easy, so we just cast the constructor
    return atLeastOneNonValueNode
      ? new (nodeClass as new (
          _arg: typeof arg,
        ) => InstanceType<ApplicableNodeClass>)(arg)
      : new expressionNode.ForValue(result)
  }
}
export namespace withProperties {
  export interface Params<
    TFn extends ApplicableFn,
    TNodeClass extends ApplicableNodeClass,
  > {
    nodeClass: TNodeClass
    fnKey: keyof TFn
    isFn: TypeChecker<TFn>
  }
}

type ApplicableFn = CreateObject | CreateTuple
type ApplicableNodeClass = typeof CreateObject.Node | typeof CreateTuple.Node

type NodeFactory = FnHelper<ApplicableFn>['createNode']
type ReturnNode = ReturnType<NodeFactory>
